import Image from "next/image";
import Link from "next/link";
import { ComponentProps } from "@components/strapi/DynamicZoneComponent";
import { HeroSectionDTO } from "@lib/strapi/dtos/components/sections/heroSection.dto";
import PhoneLink from "@components/utils/phone-link";
import useTranslation from "next-translate/useTranslation";
import { IconCalendar } from "@components/utils/icons";
import { openAppointmentModal } from "../shared-ui";

/**
 * Page hero, per the approved direction: heading left, greyscale photograph in
 * an organic mask right, phone CTA where the section opts in (items 1+6).
 *
 * Text sits on white — never on violet-400 (the contrast rule). The blob mask
 * and the grayscale filter are CSS (`blob-1`, `clinical-photo` in globals.css),
 * so editors upload ordinary colour photos.
 */
const HeroSectionImplementation = ({ section }: ComponentProps<HeroSectionDTO>) => {
  const { t } = useTranslation("main");
  if (!section) return null;
  const isHome = section.style === "Homepage";

  return (
    // NO .shell here: ModularPage already boxes contentSections — a second
    // shell double-padded the hero text 40px right of every other section
    // (caught by the owner, 2026-09-07)
    // Designer pass 2026-09-14 (hero image.psd): the homepage hero sits on a
    // full-bleed violet→white gradient with the stroke-11 scribble behind the
    // headline; both are decoration, so aria-hidden and -z behind the text.
    <section className={isHome ? "relative py-8 md:py-14" : "py-4 md:py-8"}>
      {isHome ? (
        <>
          <div aria-hidden
            className="absolute left-1/2 top-[-14rem] -z-20 h-[calc(100%+18rem)] w-screen -translate-x-1/2 bg-gradient-to-b from-violet-100 via-violet-050/60 to-transparent" />
          {/* max-w cap: at -left-40 the 34rem box pokes ~14px past the right
              viewport edge on phones — the ONE element that inflated
              document.scrollWidth and made in-app browsers zoom-fit the whole
              site (2026-09-17). 100vw + 8.5rem = 10rem offset − 1.2rem shell
              padding − slack; mask-size:contain just scales the brush down. */}
          <div aria-hidden
            className="hero-brush pointer-events-none absolute -left-40 -top-10 -z-10 h-[36rem] w-[34rem] max-w-[calc(100vw+8.5rem)] opacity-30 select-none md:-left-64 md:h-[46rem] md:w-[44rem] md:max-w-none" />
        </>
      ) : null}
      <div className={`grid items-center gap-10 ${section.image ? "lg:grid-cols-[1.1fr_0.9fr]" : ""}`}>
        <div className="max-w-2xl">
          {isHome ? (
            <h1 className="text-4xl font-extrabold tracking-tight text-ink md:text-5xl">
              {section.heading}
            </h1>
          ) : (
            <h2 className="text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
              {section.heading}
            </h2>
          )}
          {/* the lede reads, it doesn't label — serif (owner, 2026-09-09) */}
          {section.subheading ? (
            <p className="mt-4 font-serif text-lg text-muted">{section.subheading}</p>
          ) : null}
          <div className="mt-7 flex flex-wrap items-center gap-3">
            {/* the artifact hero carries TWO actions: «Καλέστε μας» (label,
                not the raw number — the header already shows it) + the
                free-assessment door into the booking dialog (D47) */}
            {section.showPhoneCta ? <PhoneLink variant="primary" placement="hero" showNumber={false} /> : null}
            {section.showPhoneCta ? (
              <button type="button" onClick={() => openAppointmentModal('hero')}
                className="inline-flex items-center gap-2 rounded-full border-2 border-violet-700 px-6 py-3 font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
                <IconCalendar width={17} height={17} aria-hidden />
                {t("cta.freeAssessmentShort")}
              </button>
            ) : null}
            {(section.links ?? []).map((link, i) => (
              <Link
                key={i}
                href={link.url ?? "#"}
                className="inline-flex items-center gap-2 rounded-full border-2 border-violet-700 px-6 py-3 font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white"
              >
                {link.title}
              </Link>
            ))}
          </div>
        </div>

        {section.image ? (
          // homepage: the designer's blob (shape-4) in FULL COLOUR — the hero
          // photo sells the treatment moment, warmth wins over the clinical
          // grayscale here; inner pages keep the calm blob-1 grayscale.
          <div className={`relative mx-auto aspect-square w-full overflow-hidden ${
            isHome ? "blob-4 max-w-lg bg-violet-050" : "blob-1 max-w-md bg-violet-400"}`}>
            <Image
              src={section.image.url}
              alt={section.image.alternativeText ?? section.heading ?? ""}
              fill
              sizes="(min-width: 1024px) 32rem, 90vw"
              quality={isHome ? 85 : 75}
              className={isHome ? "hero-photo" : "clinical-photo"}
              priority={isHome}
              // priority alone preloads at default priority; under mobile
              // throttling the hero then queues behind fonts/JS/data and LCP
              // lands ~9.5s (Lighthouse lcp-discovery, 2026-09-14). high wins
              // the bandwidth race — it IS the LCP element on the homepage.
              fetchPriority={isHome ? "high" : undefined}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HeroSectionImplementation;
